import FileDownloaderButton from 'components/FileDownloaderButton';
import IconButton from 'components/IconButton';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { showFailureToast, showSuccessToast } from 'lib/ToastHelper';
import React, { useCallback, useEffect } from 'react';
import { Badge, Col } from 'reactstrap';
import '../styles.css';

import { appliedIntegrationIdsAtom } from '../../state';
import {
  integrationDataAtom,
  integrationQueryParamsAtom,
  selectedRowIdsAtom,
} from '../state';

const IntegrationListingBulkActions: React.FC = () => {
  const [selectedRowIds, setSelectedRowIds] = useAtom(selectedRowIdsAtom);
  const [appliedRowIds, setAppliedRowIds] = useAtom(appliedIntegrationIdsAtom);
  const setListingData = useSetAtom(integrationDataAtom);
  const queryParams = useAtomValue(integrationQueryParamsAtom);

  const selectionChanged = (): boolean => {
    return (
      appliedRowIds.slice().sort().join(',') !==
      selectedRowIds.slice().sort().join(',')
    );
  };

  const markAppliedRows = useCallback(
    (rowIds: number[]) => {
      setListingData((prev) => {
        if (!prev?.results) return prev;

        let changed = false;
        const newResults = prev.results.map((row) => {
          const isApplied = rowIds.includes(row.id);
          if (row.isAppliedForDashboard !== isApplied) {
            changed = true;
            return { ...row, isAppliedForDashboard: isApplied };
          }
          return row;
        });

        if (!changed) return prev;

        return {
          ...prev,
          results: newResults,
        };
      });
    },
    [setListingData],
  );

  useEffect(() => {
    markAppliedRows(appliedRowIds);
  }, [appliedRowIds, markAppliedRows]);

  const onUpdateDashboardClick = () => {
    markAppliedRows(selectedRowIds);
    setAppliedRowIds(selectedRowIds);
  };

  return (
    <Col className="integration-listing-bulk-actions">
      {selectedRowIds?.length > 0 && (
        <Col
          md="auto"
          className="integration-listing-bulk-actions-selected text-nowrap"
        >
          {selectedRowIds?.length > 0 && (
            <>
              ( Selected:{' '}
              <label className="integration-listing-bulk-actions-label label btn">
                {selectedRowIds.length}
              </label>
              )
            </>
          )}
        </Col>
      )}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        {selectedRowIds?.length > 0 && (
          <>
            <IconButton
              id="clearSelectionButton"
              iconClass="fas fa-times"
              tooltip="Clear all selections"
              onClick={() => setSelectedRowIds([])}
            />
            <div className="integration-listing-bulk-actions-divider" />
          </>
        )}
        <IconButton
          id="updateButton"
          iconClass="fa fa-chart-line fa-lg"
          tooltip="Update Dashboard For Selected Integrations"
          disabled={
            !selectionChanged() ||
            (selectedRowIds.length === 0 && appliedRowIds.length === 0)
          }
          active={appliedRowIds.length > 0}
          stale={selectionChanged()}
          onClick={onUpdateDashboardClick}
        />
        <div style={{ position: 'relative' }} className="d-inline">
          <IconButton
            id="resetButton"
            iconClass="fa fa-undo fa-lg"
            tooltip={
              appliedRowIds?.length > 0 ? (
                <>
                  Reset dashboard to show all integrations
                  <br />
                  Applied ( {appliedRowIds.length} integrations )
                </>
              ) : (
                'Reset dashboard to show all integrations'
              )
            }
            disabled={appliedRowIds.length === 0}
            onClick={() => {
              markAppliedRows([]);
              setAppliedRowIds([]);
            }}
          />
          {appliedRowIds?.length > 0 && (
            <Badge
              color="transparent"
              pill
              className="integration-listing-bulk-actions-badge"
            >
              {appliedRowIds.length}
            </Badge>
          )}
        </div>
        <FileDownloaderButton
          buttonId="integration_export_csv"
          iconClass="fa fa-file-export"
          tooltip="Export CSV data for Integrations"
          method={selectedRowIds.length > 0 ? 'POST' : 'GET'}
          fileName={`${
            localStorage.getItem('currentCompany')?.toLowerCase() || 'company'
          }_integrations.csv`}
          downloadUrl="bpa/integrations/export-csv/"
          urlParams={queryParams}
          data={
            selectedRowIds.length > 0
              ? { integration_ids: selectedRowIds }
              : null
          }
          onSuccessCallback={() =>
            showSuccessToast('Success! File download completed.')
          }
          onFailureCallback={(message) => showFailureToast('Error! ' + message)}
        />
      </div>
    </Col>
  );
};

export default IntegrationListingBulkActions;
